import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

// Метка «проверить» в компактной строке ленты: по клику — причина и действия «Проверено» / «Открыть операцию»
export const ReviewBadge: React.FC<{ note: string; onReviewed?: () => void; onOpen?: () => void }> = ({ note, onReviewed, onOpen }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; width: number }>({ left: 0, top: 0, width: 288 });
  const ref = useRef<HTMLSpanElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  // Позиция — в пределах экрана (на телефоне метка бывает у левого края)
  const place = () => {
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(288, window.innerWidth - 16);
    setPos({ width, top: r.bottom + 4, left: Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8)) });
  };
  const toggle = () => { place(); setOpen(o => !o); };
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | TouchEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const hide = place;  // при прокрутке — за меткой
    document.addEventListener('mousedown', close); document.addEventListener('touchstart', close); document.addEventListener('keydown', esc);
    window.addEventListener('scroll', hide, true); window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', hide, true); window.removeEventListener('resize', hide);
    };
  }, [open]);
  return (
    <span ref={ref} className="relative inline-flex">
      <button ref={btn} type="button" onClick={toggle} aria-expanded={open}
        className="text-[10px] text-amber-800 bg-amber-50 hover:bg-amber-100 px-1 rounded underline decoration-dotted">проверить</button>
      {open && (
        <span role="dialog" style={{ left: pos.left, top: pos.top, width: pos.width }} className="fixed z-40 block rounded-lg border border-amber-200 bg-white p-2 shadow-lg text-[11px] text-slate-700 whitespace-normal">
          <span className="flex gap-1.5 text-amber-900"><AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span className="min-w-0 break-words">{note}</span></span>
          <span className="mt-2 flex flex-wrap justify-end gap-2">
            {onOpen && <button type="button" className="text-blue-700 hover:underline" onClick={() => { setOpen(false); onOpen(); }}>Открыть операцию</button>}
            {onReviewed && <button type="button" className="rounded bg-emerald-600 px-2 py-0.5 text-white hover:bg-emerald-700"
              onClick={() => { setOpen(false); onReviewed(); }}>Проверено</button>}
          </span>
        </span>
      )}
    </span>
  );
};

// В форме редактирования: пометка и чекбокс «Проверено» (снимает её при сохранении)
export const ReviewCheckbox: React.FC<{ note?: string | null; checked: boolean; onChange: (v: boolean) => void }> = ({ note, checked, onChange }) => {
  if (!note) return null;
  return (
    <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 space-y-1">
      <div className="flex gap-1.5"><AlertTriangle size={12} className="mt-0.5 shrink-0" /><span className="min-w-0 break-words">{note}</span></div>
      <label className="flex items-center gap-1.5 cursor-pointer text-slate-700">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} /> Проверено — снять пометку при сохранении
      </label>
    </div>
  );
};

// Перевод между ИП: правится парой (через удаление и новый перевод), поэтому здесь — только просмотр и «Проверено»
export const TransferReviewDialog: React.FC<{
  title: string; lines: [string, string][]; note?: string | null; onClose: () => void; onReviewed: () => void;
}> = ({ title, lines, note, onClose, onReviewed }) => {
  const [checked, setChecked] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl text-sm space-y-3">
        <h3 className="font-bold">{title}</h3>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          {lines.map(([k, v]) => <React.Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="min-w-0 break-words">{v}</dd></React.Fragment>)}
        </dl>
        <p className="text-[11px] text-slate-500">Перевод между ИП проводится парой операций; чтобы изменить сумму или дату — удалите его и проведите заново.</p>
        <ReviewCheckbox note={note} checked={checked} onChange={setChecked} />
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button type="button" className="rounded px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200" onClick={onClose}>Закрыть</button>
          {note && <button type="button" disabled={!checked} className="rounded px-3 py-1 text-xs bg-blue-600 text-white disabled:opacity-40"
            onClick={() => { onReviewed(); onClose(); }}>Сохранить</button>}
        </div>
      </div>
    </div>
  );
};
